<template>

    <Toast/>

    <MenuHealthCompact/>

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Configuration</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Practitioners</a></li>
                    </ul>

                    <h3>Manage Practitioners</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewPractitioner" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Practitioner</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="practitioners"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['firstName', 'lastName', 'registrationNumber', 'practiceNumber', 'email', 'mobile']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on First Name, Last Name, Registration #, Practice #, Email or Mobile"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No practitioners found.
                        </template>

                        <template #loading>
                            Loading practitioners. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItems(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                </template>
                            </Column>

                            <Column v-if="col.field === 'category'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Category</span>
                                    <span v-if="data.category === 'I'"
                                          :class="'category-badge status-' + data.category">Internal</span>
                                    <span v-if="data.category === 'R'"
                                          :class="'category-badge status-' + data.category">Referring</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'type'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Type</span>
                                    <span v-if="data.type === 'A'">Occupational Therapist</span>
                                    <span v-if="data.type === 'B'">Clinical Psychologist</span>
                                    <span v-if="data.type === 'C'">Counselling Psychologist</span>
                                    <span v-if="data.type === 'D'">Educational Psychologist</span>
                                    <span v-if="data.type === 'E'">Industrial Psychologist</span>
                                    <span v-if="data.type === 'F'">Psychologist</span>
                                    <span v-if="data.type === 'G'">Psychiatrist</span>
                                    <span v-if="data.type === 'H'">Social Worker</span>
                                    <span v-if="data.type === 'I'">Registered Counsellor</span>
                                    <span v-if="data.type === 'J'">Biokineticist</span>
                                    <span v-if="data.type === 'K'">Physiotherapist</span>
                                    <span v-if="data.type === 'L'">Speech Therapist</span>
                                    <span v-if="data.type === 'M'">Audiologist</span>
                                    <span v-if="data.type === 'N'">Speech Therapist and Audiologist</span>
                                    <span v-if="data.type === 'O'">Dietician</span>
                                    <span v-if="data.type === 'P'">Chiropractor</span>
                                    <span v-if="data.type === 'Q'">Podiatrist</span>
                                    <span v-if="data.type === 'R'">GP</span>
                                    <span v-if="data.type === 'S'">Dentist</span>
                                    <span v-if="data.type === 'T'">Specialist Physician</span>
                                    <span v-if="data.type === 'U'">Other</span>
                                    <span v-if="data.type === 'V'">Registered Nurse</span>
                                    <span v-if="data.type === 'W'">Anaesthetist</span>
                                    <span v-if="data.type === 'X'">Gynaecologist</span>
                                    <span v-if="data.type === 'Y'">Ocularist</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Practitioner {{ this.practitioner.firstName + " " + this.practitioner.lastName }}</h3>

                    <div class="field">
                        <label for="type">Practitioner Type</label>
                        <Dropdown v-model="practitioner.type" :options="practitionerTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Practitioner Type"/>
                    </div>

                    <div class="field">
                        <label for="firstName">First Name</label>
                        <InputText id="firstName" v-model.trim="practitioner.firstName" required="true"/>

                        <small v-if="!practitioner.firstName" class="p-error">First Name is required.</small>
                    </div>

                    <div class="field">
                        <label for="lastName">Last Name</label>
                        <InputText id="lastName" v-model.trim="practitioner.lastName" required="true"/>

                        <small v-if="!practitioner.lastName" class="p-error">Last Name is required.</small>
                    </div>

                    <div class="field">
                        <label for="qualification">Qualification</label>
                        <InputText id="qualification" v-model.trim="practitioner.qualification"
                                   required="true"/>

                        <small v-if="!practitioner.qualification" class="p-error">Qualification is required.</small>
                    </div>

                    <div class="field">
                        <label for="registrationNumber">Registration Number</label>
                        <InputText id="registrationNumber" v-model.trim="practitioner.registrationNumber"
                                   required="true"/>

                        <small v-if="!practitioner.registrationNumber" class="p-error">Registration Number is required.</small>
                    </div>

                    <div class="field">
                        <label for="practiceNumber">Practice Number</label>
                        <InputText id="practiceNumber" v-model.trim="practitioner.practiceNumber"
                                   required="true"/>

                        <small v-if="!practitioner.practiceNumber" class="p-error">Practice Number is required.</small>
                    </div>

                    <div class="field">
                        <label for="email">Email</label>
                        <InputText id="email" v-model.trim="practitioner.email" required="true"/>

                        <small v-if="!practitioner.email" class="p-error">Email is required.</small>
                        <small v-else-if="emailError" class="p-error">Invalid email format.</small>
                    </div>

                    <div class="field">
                        <label for="mobile">Mobile Number</label>
                        <InputText id="mobile" v-model.trim="practitioner.mobile" required="true"/>
                    </div>

                    <div class="field">
                        <label for="onlineBookingLink">Online Booking Link</label>
                        <InputText id="onlineBookingLink" v-model.trim="practitioner.onlineBookingLink" required="true"/>
                    </div>

                    <div class="field">
                        <label for="status">Category</label>
                        <Dropdown v-model="practitioner.category" :options="categoryTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Category"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="practitioner.status" :options="statusTypes" optionLabel="name"
                                  optionValue="code"
                                  placeholder="Select a Status"/>
                    </div>

                    <div class="field">
                        <label for="supervisor">Supervisor</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="practitioner.supervisorName"
                                      :suggestions="filteredPractitioners"
                                      @complete="searchPractitioners($event)" field="fullName"/>
                    </div>

                    <Button label="Update" :disabled="!isExistingPractitionerFormValid" class="mr-2 mb-2" @click="updatePractitioner"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Practitioner {{ this.practitioner.firstName + " " + this.practitioner.lastName }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Practitioner?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deletePractitioner"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Practitioner</h3>

                    <div class="field">
                        <label for="type">Practitioner Type</label>
                        <Dropdown v-model="newPractitioner.type" :options="practitionerTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Practitioner Type"/>
                    </div>

                    <div class="field">
                        <label for="type">Category</label>
                        <Dropdown v-model="newPractitioner.category" :options="categoryTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Category"/>
                    </div>

                    <div class="field">
                        <label for="firstName">First Name</label>
                        <InputText id="firstName" v-model.trim="newPractitioner.firstName" required="true" @blur="touched.firstName = true"/>

                        <small v-if="!newPractitioner.firstName" class="p-error">First Name is required.</small>
                    </div>

                    <div class="field">
                        <label for="lastName">Last Name</label>
                        <InputText id="lastName" v-model.trim="newPractitioner.lastName" required="true"/>

                        <small v-if="!newPractitioner.lastName" class="p-error">Last Name is required.</small>
                    </div>

                    <div class="field">
                        <label for="qualification">Qualification</label>
                        <InputText id="qualification" v-model.trim="newPractitioner.qualification"
                                   required="true"/>

                        <small v-if="!newPractitioner.qualification" class="p-error">Qualification is required.</small>
                    </div>

                    <div class="field">
                        <label for="registrationNumber">Registration Number</label>
                        <InputText id="registrationNumber" v-model.trim="newPractitioner.registrationNumber"
                                   required="true"/>

                        <small v-if="!newPractitioner.registrationNumber" class="p-error">Registration Number is required.</small>
                    </div>

                    <div class="field">
                        <label for="practiceNumber">Practice Number</label>
                        <InputText id="practiceNumber" v-model.trim="newPractitioner.practiceNumber"
                                   required="true"/>

                        <small v-if="!newPractitioner.practiceNumber" class="p-error">Practice Number is required.</small>
                    </div>

                    <div class="field">
                        <label for="email">Email</label>
                        <InputText id="email" v-model.trim="newPractitioner.email" required="true"/>

                        <small v-if="!newPractitioner.email" class="p-error">Email is required.</small>
                        <small v-else-if="emailError" class="p-error">Invalid email format.</small>
                    </div>

                    <div class="field">
                        <label for="mobile">Mobile Number</label>
                        <InputText id="mobile" v-model.trim="newPractitioner.mobile" required="true"/>
                    </div>

                    <div class="field">
                        <label for="supervisor">Supervisor</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newPractitioner.supervisorName"
                                      :suggestions="filteredPractitioners"
                                      @complete="searchPractitioners($event)" field="fullName"/>
                    </div>

                    <Button label="Create" :disabled="!isNewPractitionerFormValid" class="mr-2 mb-2" @click="createPractitioner"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Set Availability for Practitioner</h3>

                    <div class="field">
                        <label for="digitalBookingLink">Digital Booking Link</label>
                        <InputText id="digitalBookingLink" type="text" :disabled="true" v-model.trim="digitalBookingLink"/>
                    </div>

                    <br>

                    <div v-for="day in daysOfWeek" :key="day.value">
                        <h3>{{ day.label }}</h3>
                        <div class="hour-buttons">
                            <button
                                v-for="hour in 24"
                                :key="`${day.value}-${hour}`"
                                @click="updateAvailability(day.value, hour)"
                                :class="{ 'available': isAvailable(day.value, hour) }">
                                {{ formatHour(hour) }}
                            </button>
                        </div>
                    </div>


                    <div>&nbsp;</div>
                    <div>&nbsp;</div>

                    <Button label="Save" class="mr-2 mb-2" @click="saveAvailability"/>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"/>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import PractitionerService from "@/service/PractitionerService";
import CompanyService from "@/service/CompanyService";
import GenericAvailabilityService from "@/service/GenericAvailabilityService";

export default {
    components: {
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'firstName', header: 'First Name', sortable: true},
            {field: 'lastName', header: 'Last Name', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'category', header: 'Category', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'registrationNumber', header: 'Registration #', sortable: true},
            {field: 'practiceNumber', header: 'Practice #', sortable: true},
        ];

        this.columnsFullList = [
            {field: 'id', header: 'ID', sortable: true},
            {field: 'firstName', header: 'First Name', sortable: true},
            {field: 'lastName', header: 'Last Name', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'category', header: 'Category', sortable: true},
            {field: 'status', header: 'Status', sortable: true},
            {field: 'registrationNumber', header: 'Registration #', sortable: true},
            {field: 'practiceNumber', header: 'Practice #', sortable: true},
            {field: 'email', header: 'Email', sortable: true},
            {field: 'mobile', header: 'Mobile', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'active', header: 'Active'},
            {field: 'inactive', header: 'Inactive'}
        ];

    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            emailError: false,
            digitalBookingLink: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            practitioners: null,
            originalPractitioners: null,
            practitioner: {
                type: '',
                firstName: '',
                lastName: '',
                qualification: '',
                registrationNumber: '',
                practiceNumber: '',
                email: '',
                mobile: '',
                supervisorName: ''
            },
            newPractitioner: {
                type: '',
                firstName: '',
                lastName: '',
                qualification: '',
                registrationNumber: '',
                practiceNumber: '',
                email: '',
                mobile: '',
                supervisorName: ''
            },
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            filteredPractitioners: [],
            selectedAvailability: [], // Store availability as a flat array
            selectedBookingSlot: null, // Store selected slot {date, time}
            availableDatesList: [], // Stores available dates with slots
            selectedBookingDate: null, // Store selected date instead of slots
            daysOfWeek: [
                {label: "Monday", value: 1},
                {label: "Tuesday", value: 2},
                {label: "Wednesday", value: 3},
                {label: "Thursday", value: 4},
                {label: "Friday", value: 5},
                {label: "Saturday", value: 6},
                {label: "Sunday", value: 7},
            ],
            practitionerTypes: [
                {name: 'Occupational Therapist', code: 'A'},
                {name: 'Clinical Psychologist', code: 'B'},
                {name: 'Counselling Psychologist', code: 'C'},
                {name: 'Educational Psychologist', code: 'D'},
                {name: 'Industrial Psychologist', code: 'E'},
                {name: 'Psychologist', code: 'F'},
                {name: 'Psychiatrist', code: 'G'},
                {name: 'Social Worker', code: 'H'},
                {name: 'Registered Counsellor', code: 'I'},
                {name: 'Biokineticist', code: 'J'},
                {name: 'Physiotherapist', code: 'K'},
                {name: 'Speech Therapist', code: 'L'},
                {name: 'Audiologist', code: 'M'},
                {name: 'Speech Therapist and Audiologist', code: 'N'},
                {name: 'Dietician', code: 'O'},
                {name: 'Chiropractor', code: 'P'},
                {name: 'Podiatrist', code: 'Q'},
                {name: 'GP', code: 'R'},
                {name: 'Dentist', code: 'S'},
                {name: 'Specialist Physician', code: 'T'},
                {name: 'Registered Nurse', code: 'V'},
                {name: 'Anaesthetist', code: 'W'},
                {name: 'Gynaecologist', code: 'X'},
                {name: 'Ocularist', code: 'Y'},
                {name: 'Other', code: 'U'},
            ],
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            categoryTypes: [
                {name: 'Internal', code: 'I'},
                {name: 'Referring', code: 'R'}
            ]
        }
    },
    watch: {
        practitioners() {
            this.filteredItemsCache.clear();
        },
        'newPractitioner.email'(val) {
            this.emailError = val ? !this.isValidEmail(val) : false;
        },
        'practitioner.email'(val) {
            this.emailError = val ? !this.isValidEmail(val) : false;
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                await this.loadCompanyContext();
            }
        }
    },
    computed: {
        isNewPractitionerFormValid() {
            return (
                this.newPractitioner.type &&
                this.newPractitioner.category &&
                this.newPractitioner.firstName &&
                this.newPractitioner.lastName &&
                this.newPractitioner.qualification &&
                this.newPractitioner.registrationNumber &&
                this.newPractitioner.practiceNumber &&
                this.newPractitioner.email &&
                !this.emailError
            );
        },
        isExistingPractitionerFormValid() {
            return (
                this.practitioner.type &&
                this.practitioner.category &&
                this.practitioner.firstName &&
                this.practitioner.lastName &&
                this.practitioner.qualification &&
                this.practitioner.registrationNumber &&
                this.practitioner.practiceNumber &&
                this.practitioner.email &&
                !this.emailError
            );
        },
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            const [userConfigData] = await Promise.all([
                UserService.getUserConfig(this.userStore.user.id)
            ]);

            this.userConfiguration = userConfigData.data;

            const [practitionerData, companyConfigData] = await Promise.all([
                PractitionerService.getAllCompanyPractitioners(this.profile.companyId),
                CompanyService.getCompanyConfiguration(this.profile.companyId)
            ]);

            this.practitioners = practitionerData.data;
            this.originalPractitioners = practitionerData.data;

            this.companyConfiguration = companyConfigData.data;

            this.loading = false;

            this.initializeAvailability();
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'firstName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'lastName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'registrationNumber': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'practiceNumber': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'email': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'mobile': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        isValidEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        },
        callReset() {
            this.activeTab = 1;
        },
        callNewPractitioner() {
            this.activeTab = 4;
            this.newPractitioner = {};
            this.newPractitioner.category = 'I';
        },
        callUpdatePractitioner(data) {
            this.activeTab = 2;
            this.practitioner = data;
        },
        callDeletePractitioner(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.practitioner = data;
        },
        callMakeBooking(data) {
            this.activeTab = 1;
            this.confirmationResponse = 'N';
            this.practitioner = data;

            window.open(this.practitioner.onlineBookingLink, '_blank');
        },
        callSetAvailability(data) {
            this.activeTab = 5;
            this.practitioner = data;

            this.digitalBookingLink = "https://app.isoevolve.com/#/digitalbooking?company=" + this.profile.companyId + "&practitioner=" + this.practitioner.id;

            this.initializeAvailability();
            this.fetchAvailability();
        },
        searchPractitioners(event) {
            const q = (event?.query ?? '').trim().toLowerCase();

            // If dropdown was clicked with an empty input, show all (or a cached subset)
            if (!q) {
                // Option A: use an already-fetched list
                this.filteredPractitioners = (this.practitioners ?? []).slice(0, 50); // cap if large
                return;
            }

            PractitionerService.searchCompanyPractitioners(this.profile.companyId, q)
                .then(data => {
                    this.filteredPractitioners = data.data;
                })
                .catch(() => {
                    this.filteredPractitioners = [];
                });

        },
        async fetchAvailableSlots() {
            const response = await GenericAvailabilityService.getAvailableSlots(
                this.profile.companyId,
                this.practitioner.id
            );

            if (!response || !response.data || !Array.isArray(response.data)) {
                this.availableDatesList = [];
                return;
            }

            const today = new Date();
            const nextTwoWeeks = new Date();
            nextTwoWeeks.setDate(today.getDate() + 14); // 2 weeks from today

            let availableDatesMap = new Map();

            response.data.forEach((slot) => {
                const slotDate = this.getNextDateForDay(slot.dayOfWeek, today, nextTwoWeeks);

                if (slotDate) {
                    const formattedDate = slotDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD

                    if (!availableDatesMap.has(formattedDate)) {
                        availableDatesMap.set(formattedDate, []);
                    }

                    availableDatesMap.get(formattedDate).push(this.formatHour(slot.hour));
                }
            });

            // Convert Map to Array, sort by date, and store it in availableDatesList
            this.availableDatesList = Array.from(availableDatesMap, ([date, hours]) => ({
                date,
                availableSlots: hours.sort((a, b) => this.parseHour(a) - this.parseHour(b))
            })).sort((a, b) => new Date(a.date) - new Date(b.date));

        },
        getNextDateForDay(dayOfWeek, startDate, endDate) {
            let date = new Date(startDate);

            while (date <= endDate) {
                if (date.getDay() === (dayOfWeek % 7)) { // Adjust for JavaScript's 0-based week (Sunday = 0)
                    return new Date(date); // Return a copy of the matching date
                }
                date.setDate(date.getDate() + 1);
            }

            return null; // No valid date found within the range
        },
        parseHour(timeString) {
            let match = timeString.match(/(\d+)(?:\s?(AM|PM))?/);
            if (!match) return 0;

            let hour = parseInt(match[1], 10);
            let period = match[2];

            if (period === "PM" && hour !== 12) hour += 12;
            if (period === "AM" && hour === 12) hour = 0;

            return hour;
        },
        selectBookingDate(date) {
            this.selectedBookingDate = date;
        },
        selectBookingSlot(date, time) {
            this.selectedBookingSlot = {date, time};
        },
        isSelectedSlot(date, time) {
            return this.selectedBookingSlot && this.selectedBookingSlot.date === date && this.selectedBookingSlot.time === time;
        },
        async bookAppointment() {
            if (!this.selectedBookingSlot) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'No Slot Selected',
                    detail: 'Please select a time slot before booking.',
                    life: 3000
                });
                return;
            }

            try {
                this.$toast.add({
                    severity: 'success',
                    summary: 'Appointment Booked',
                    detail: `Your appointment for ${this.formatDate(this.selectedBookingSlot.date)} at ${this.selectedBookingSlot.time} has been successfully booked.`,
                    life: 3000
                });

                this.selectedBookingSlot = null;
                this.fetchAvailableSlots(); // Refresh available dates

            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Booking Failed',
                    detail: 'Could not book the appointment. Try again later.',
                    life: 3000
                });
            }
        },
        formatDate(dateString) {
            const options = {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'};
            return new Date(dateString).toLocaleDateString(undefined, options);
        },
        // formatDate(dateString) {
        //     const options = { year: 'numeric', month: 'long', day: 'numeric' };
        //     return new Date(dateString).toLocaleDateString(undefined, options);
        // },
        formatHour(hour) {
            if (hour === 0) return "12 AM";
            if (hour < 12) return `${hour} AM`;
            if (hour === 12) return "12 PM";
            return `${hour - 12} PM`;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        }
        ,
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.practitioners !== null) {

                if (isActive) {
                    this.practitioners = this.originalPractitioners.filter(practitioner => practitioner.status === 'A');
                } else if (isInactive) {
                    this.practitioners = this.originalPractitioners.filter(practitioner => practitioner.status === 'I');
                } else {
                    this.practitioners = this.originalPractitioners;
                }

            } else {
                // Handle the case when auditTrails is null
                this.practitioners = null;
            }
        }
        ,
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdatePractitioner(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeletePractitioner(data);
                }
            });

            items.push({
                label: 'Make Booking',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callMakeBooking(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        }
        ,
        chooseBlankSelection() {
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        }
        ,
        initializeAvailability() {
            this.selectedAvailability = [];

            this.daysOfWeek.forEach((day) => {
                for (let hour = 0; hour < 24; hour++) {
                    this.selectedAvailability.push({
                        dayOfWeek: day.value,
                        hour: hour,
                        available: false, // Default to unavailable
                    });
                }
            });
        }
        ,
        async fetchAvailability() {
            const response = await GenericAvailabilityService.getAvailabilityByPractitioner(
                this.profile.companyId,
                this.practitioner.id
            );

            if (!response || !response.data || !Array.isArray(response.data)) {
                return;
            }

            response.data.forEach((item) => {
                const availability = this.selectedAvailability.find(
                    (a) => a.dayOfWeek === item.dayOfWeek && a.hour === item.hour
                );
                if (availability) {
                    availability.available = item.status === "A";
                }
            });

        }
        ,
        isAvailable(day, hour) {
            return this.selectedAvailability.some(
                (a) => a.dayOfWeek === day && a.hour === hour && a.available
            );
        }
        ,
        updateAvailability(day, hour) {
            const availability = this.selectedAvailability.find(
                (a) => a.dayOfWeek === day && a.hour === hour
            );

            if (availability) {
                availability.available = !availability.available; // Toggle the availability
            }
        }
        ,
// updateAvailability(day, hour) {
//     if (!this.selectedAvailability[day]) {
//         // Ensure day exists before modifying it
//         this.selectedAvailability[day] = Array(24).fill(false);
//     }
//     this.selectedAvailability[day][hour] = !this.selectedAvailability[day][hour];
// },
        async saveAvailability() {
            const formattedData = this.selectedAvailability
                .filter(item => item.available) // Get only selected (available) slots
                .map(item => ({
                    practitionerId: this.practitioner.id, // Change dynamically
                    dayOfWeek: item.dayOfWeek,
                    hour: item.hour,
                    status: "A",
                }));

            await GenericAvailabilityService.saveAvailabilityInBatch(
                this.profile.companyId,
                this.practitioner.id,
                formattedData
            );

            this.$toast.add({
                severity: 'success',
                summary: 'Successful',
                detail: 'Availability updated',
                life: 3000
            });
        },
        async updatePractitioner() {
            this.submitted = true;

            if (this.practitioner.id) {

                try {

                    var tempSupervisor = "";

                    try {
                        if (this.practitioner.supervisorName === null || this.practitioner.supervisorName.length === 0) {
                            tempSupervisor = this.practitioner.supervisorId;
                        } else {
                            tempSupervisor = this.practitioner.supervisorName.id;
                        }

                    } catch (error) {
                        tempSupervisor = this.practitioner.supervisorId;
                    }

                    var practitionerDto = {
                        type: this.practitioner.type,
                        category: this.practitioner.category,
                        firstName: this.practitioner.firstName,
                        lastName: this.practitioner.lastName,
                        qualification: this.practitioner.qualification,
                        registrationNumber: this.practitioner.registrationNumber,
                        practiceNumber: this.practitioner.practiceNumber,
                        email: this.practitioner.email,
                        mobile: this.practitioner.mobile,
                        onlineBookingLink: this.practitioner.onlineBookingLink,
                        status: this.practitioner.status,
                        supervisorId: tempSupervisor
                    };

                    await PractitionerService.updatePractitioner(this.practitioner.id, practitionerDto);

                    const [practitionerData] = await Promise.all([
                        PractitionerService.getAllCompanyPractitioners(this.profile.companyId)
                    ]);

                    this.practitioners = practitionerData.data;
                    this.originalPractitioners = practitionerData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated practitioner',
                        life: 3000
                    });

                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update practitioner. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        }
        ,
        async deletePractitioner() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.practitioner.id) {

                    try {
                        await PractitionerService.deletePractitioner(this.practitioner.id);

                        const [practitionerData] = await Promise.all([
                            PractitionerService.getAllCompanyPractitioners(this.profile.companyId)
                        ]);

                        this.practitioners = practitionerData.data;
                        this.originalPractitioners = practitionerData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted practitioner',
                            life: 3000
                        });

                    } catch (error) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete practitioner. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        }
        ,
        async createPractitioner() {
            this.submitted = true;

            if (this.newPractitioner.firstName) {

                var tempSupervisor = "";
                try {
                    if (this.newPractitioner.supervisorName === null || this.newPractitioner.supervisorName.length === 0) {
                        tempSupervisor = null;
                    } else {
                        tempSupervisor = this.newPractitioner.supervisorName.id;
                    }

                } catch (error) {
                    tempSupervisor = null;
                }

                try {
                    var newPractitionerDto = {
                        type: this.newPractitioner.type,
                        category: this.newPractitioner.category,
                        firstName: this.newPractitioner.firstName,
                        lastName: this.newPractitioner.lastName,
                        qualification: this.newPractitioner.qualification,
                        registrationNumber: this.newPractitioner.registrationNumber,
                        practiceNumber: this.newPractitioner.practiceNumber,
                        email: this.newPractitioner.email,
                        mobile: this.newPractitioner.mobile,
                        status: 'A',
                        supervisorId: tempSupervisor,
                    };

                    await PractitionerService.createPractitioner(this.profile.companyId, newPractitionerDto);

                    const [practitionerData] = await Promise.all([
                        PractitionerService.getAllCompanyPractitioners(this.profile.companyId)
                    ]);

                    this.practitioners = practitionerData.data;
                    this.originalPractitioners = practitionerData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created practitioner',
                        life: 3000
                    });

                } catch (error) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create practitioner. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
}
;
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

//.hour-buttons {
//    display: flex;
//    flex-wrap: wrap;
//    gap: 5px;
//}

//button {
//    padding: 5px 10px;
//    border: 1px solid #ddd;
//    background-color: #f5f5f5;
//    cursor: pointer;
//}

//button.available {
//    background-color: green;
//    color: white;
//}

.availability-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 5px;
    align-items: center;
}

.grid-header {
    display: contents;
    font-weight: bold;
}

.day-header {
    text-align: left;
}

.grid-row {
    display: contents;
}

.hour-label {
    text-align: right;
    padding-right: 10px;
}

.status-badge, .type-badge, .category-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-I {
        background: #FEEDAF;
        color: #8A5340;
    }
}

.category-badge {
    &.status-I {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-R {
        background: #FEEDAF;
        color: #8A5340;
    }
}

.type-badge {
    &.status-C {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-S {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-I {
        background: #bc91d5;
        color: #71038f;
    }

}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

//.mb-2 {
//    margin-bottom: 1rem;
//}
//
//.mr-2 {
//    margin-right: 1rem;
//}

</style>
